import type { Dimension, Question } from "../engine/types";

export const dimensionLabels: Record<Dimension, string> = {
  visibility: "Portfolio decisions",
  governance: "Safe automation",
  optimization: "Change evidence",
  commitments: "Financial risk",
  architecture: "Migration readiness",
  operatingModel: "Value realization",
};

const option = (value: string, label: string, description: string, score: number, ...signals: string[]) => ({ value, label, description, score, signals });

export const questions: Question[] = [
  {
    id: "ownerMapping", dimension: "visibility",
    prompt: "Can the material recommendations in this export be routed to people who can decide and act?",
    help: "AWS identifies the account and resource. We need to understand whether business and technical ownership is usable in practice.",
    options: [
      option("central", "The cloud team has to find owners", "Ownership depends on institutional knowledge and repeated follow-up.", 0, "ownership-gap"),
      option("partial", "Owners are known for priority workloads", "The largest opportunities can be routed, but the recommendation tail is inconsistent.", 2, "partial-ownership"),
      option("most", "Most material workloads have accountable owners", "Large recommendations reach people who can assess risk and schedule change.", 3, "owner-foundation"),
      option("full", "Ownership is encoded and governed", "Routing, escalation, and exceptions follow repeatable rules.", 4, "owner-strength"),
    ],
  },
  {
    id: "materialityThreshold", dimension: "visibility",
    prompt: "Which recommendations deserve individual human review?",
    help: "The imported data shows value and effort. Your policy decides what becomes a decision, grouped change, automation, or no action.",
    options: [
      option("none", "Every recommendation enters the same queue", "Small, safe actions compete with material and risky decisions.", 0, "threshold-gap"),
      option("adhoc", "Reviewers decide case by case", "Prioritization varies by analyst, team, or meeting.", 1, "threshold-adhoc"),
      option("savings", "A minimum savings threshold exists", "Small items are grouped unless risk or strategic importance requires review.", 3, "threshold-foundation"),
      option("portfolio", "Value, effort, and risk determine the path", "Financial decisions, engineering changes, migrations, and automation are separated.", 4, "portfolio-rules"),
    ],
  },
  {
    id: "deletionGuardrails", dimension: "governance",
    prompt: "What must be true before a delete or stop recommendation is approved?",
    help: "Use the answer that best reflects retention, owner confirmation, dependency checks, restart, rollback, and exceptions.",
    options: [
      option("manual", "An engineer investigates each item", "Safety depends on individual judgment and the queue moves slowly.", 1, "manual-safety"),
      option("owner", "The owner confirms the action", "Ownership is checked, but retention and dependency evidence varies.", 2, "owner-confirmation"),
      option("runbook", "A standard runbook defines the evidence", "Retention, snapshot, dependency, rollback, and exception rules are repeatable.", 3, "guardrail-foundation"),
      option("policy", "Policy approves known-safe patterns", "Low-risk actions can be grouped or automated with auditable exceptions.", 4, "guardrail-strength"),
    ],
  },
  {
    id: "automationCoverage", dimension: "governance",
    prompt: "How much recurring storage and idle-resource waste is prevented automatically?",
    help: "A one-time cleanup realizes value once. Lifecycle controls, schedules, expiry, and modern defaults keep it from returning.",
    options: [
      option("none", "Cleanup happens only after discovery", "The same avoidable patterns can recur indefinitely.", 0, "recurrence-gap"),
      option("campaigns", "Teams run periodic cleanup campaigns", "Waste is reduced, but prevention is inconsistent.", 2, "campaign-model"),
      option("partial", "Common patterns have schedules or lifecycle rules", "Known waste patterns are prevented while exceptions remain manual.", 3, "automation-foundation"),
      option("systematic", "Prevention is built into platform defaults", "Expiry, scheduling, lifecycle, and exception controls are governed continuously.", 4, "automation-strength"),
    ],
  },
  {
    id: "metricsDepth", dimension: "optimization",
    prompt: "What evidence is available to validate the high-value rightsizing recommendations?",
    help: "CPU alone rarely proves safety for memory-sensitive, latency-sensitive, stateful, or seasonal workloads.",
    options: [
      option("basic", "Basic CPU history", "Memory, network, storage, latency, and business-cycle risk may be missed.", 0, "metrics-gap"),
      option("standard", "Standard AWS service metrics", "Common utilization and performance signals can be reviewed.", 2, "metrics-basic"),
      option("extended", "Workload metrics and SLOs", "Application behavior can be tested against projected utilization and headroom.", 3, "metrics-strong"),
      option("decision", "Extended lookback plus change evidence", "Seasonality, SLOs, observation, rollback triggers, and realized cost are available.", 4, "metrics-decision-grade"),
    ],
  },
  {
    id: "changeControls", dimension: "optimization",
    prompt: "How would the organization execute and observe a material rightsizing change?",
    help: "The imported recommendation estimates value. Your operating practice determines whether that value can be realized safely.",
    options: [
      option("informal", "The workload team handles it informally", "Change, observation, rollback, and value confirmation vary by owner.", 0, "change-gap"),
      option("ticket", "A standard change ticket is used", "The action is visible, but service-specific evidence may remain inconsistent.", 2, "change-basic"),
      option("runbook", "A service-specific runbook is used", "Observation, rollback, and acceptance criteria are explicit.", 3, "change-foundation"),
      option("factory", "Validated patterns move through a rightsizing factory", "Repeatable changes are grouped while exceptions receive deeper review.", 4, "change-strength"),
    ],
  },
  {
    id: "architectureChange", dimension: "commitments",
    prompt: "How much could eligible demand change during a new commitment term?",
    help: "Modernization, acquisitions, product shifts, data-center exits, and service redesign can invalidate a point-in-time purchase recommendation.",
    options: [
      option("major", "Material change is already planned", "Coverage should remain conservative until future-state demand is modeled.", 0, "change-risk"),
      option("uncertain", "The roadmap is not stable", "There is meaningful risk of buying against demand that moves.", 1, "change-uncertainty"),
      option("moderate", "Some workloads will change", "A protected baseline may still support selective coverage.", 2, "change-moderate"),
      option("stable", "The eligible baseline should persist", "Commitment scenarios can be evaluated with greater confidence.", 4, "change-stable"),
    ],
  },
  {
    id: "commitmentGovernance", dimension: "commitments",
    prompt: "How would the commitment opportunities in this export be approved?",
    help: "The biggest apparent savings can also create the longest financial lock-in. Approval should test the protected baseline, not only the discount.",
    options: [
      option("discount", "Purchase when the discount looks attractive", "The decision is driven by a point-in-time recommendation.", 0, "commitment-risk"),
      option("finance", "Finance or procurement approves", "Technical demand assumptions may remain implicit.", 2, "financial-governance"),
      option("modeled", "Base and downside scenarios are modeled", "Owners validate the durable baseline and architecture-change case.", 3, "commitment-modeled"),
      option("portfolio", "A portfolio risk policy governs purchases", "Coverage, utilization, expiry, concentration, timing, and exceptions are explicit.", 4, "commitment-strength"),
    ],
  },
  {
    id: "gravitonReadiness", dimension: "architecture",
    prompt: "How well are compatibility and dependency differences understood for the Graviton candidates?",
    help: "The export identifies potential economics. Runtime, native dependencies, vendor support, and platform differences determine the real effort.",
    options: [
      option("unknown", "Candidates have not been classified", "The first task is a compatibility and dependency inventory.", 0, "graviton-gap"),
      option("concerns", "Known compatibility concerns dominate", "Teams need a structured path to prove or reject candidate workloads.", 1, "graviton-blocked"),
      option("pilots", "Selected workloads have been assessed", "Evidence exists, but the pattern is not repeatable yet.", 3, "graviton-pilot"),
      option("standard", "Compatibility is part of platform standards", "Known patterns and exceptions guide candidate selection.", 4, "graviton-strength"),
    ],
  },
  {
    id: "deploymentReadiness", dimension: "architecture",
    prompt: "Can candidate workloads reuse build, test, deploy, observe, and rollback patterns?",
    help: "A migration program scales through reusable delivery evidence, not through one bespoke project per workload.",
    options: [
      option("manual", "Delivery is mostly manual", "Each migration would become a separate engineering project.", 0, "delivery-gap"),
      option("mixed", "Automation varies by team", "Shared patterns exist, but reuse is inconsistent.", 2, "delivery-mixed"),
      option("automated", "Most candidates use automated delivery", "Pilots can reuse testing, observability, deployment, and rollback controls.", 3, "delivery-ready"),
      option("platform", "A paved-road platform is standard", "Architecture migrations can scale through reusable product-team patterns.", 4, "delivery-strength"),
    ],
  },
  {
    id: "deliveryOwnership", dimension: "operatingModel",
    prompt: "Who owns implementation, and how is capacity reserved for it?",
    help: "Recommendations do not realize value unless the right teams can prioritize the work against product delivery.",
    options: [
      option("none", "No owner or protected capacity", "Optimization is deferred until an escalation or incident.", 0, "capacity-gap"),
      option("central", "The central cloud team handles what it can", "Application changes still wait for product teams.", 1, "capacity-central"),
      option("planned", "Owners plan capacity for priority waves", "Material opportunities enter team planning with explicit trade-offs.", 3, "capacity-planned"),
      option("product", "Cloud economics is part of product ownership", "Cost-performance outcomes compete transparently with other product work.", 4, "capacity-strength"),
    ],
  },
  {
    id: "realizationTracking", dimension: "operatingModel",
    prompt: "How is estimated opportunity converted into verified realized value?",
    help: "AWS provides an estimate. The organization must track validation, approval, implementation, and actual cost impact separately.",
    options: [
      option("none", "Only the original estimate is reported", "Potential and realized value can be confused.", 0, "realization-gap"),
      option("closed", "Completion is tracked", "Teams know an action happened, but not whether the expected value appeared.", 2, "completion-only"),
      option("measured", "Post-change cost impact is checked", "Implemented value is compared with the estimate after an observation period.", 3, "realization-foundation"),
      option("waterfall", "A full opportunity waterfall is governed", "Identified, validated, approved, implemented, and realized value remain distinct.", 4, "realization-strength"),
    ],
  },
];

export const questionsByDimension = questions.reduce<Record<Dimension, Question[]>>(
  (groups, question) => { groups[question.dimension].push(question); return groups; },
  { visibility: [], governance: [], optimization: [], commitments: [], architecture: [], operatingModel: [] },
);
